import React, { useState, useEffect, useCallback } from "react";
import { Card, Select, DatePicker, Button, Table, Checkbox, Empty, Typography, message } from "antd";
import moment from "moment";
import { getGroups, getFacilitiesByGroup, getPeriodicalStatusReport } from "./periodicalApi";
import { isManagerUser } from "utils/navUtils";

const { Option } = Select;
const { Title } = Typography;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const monthColumns = MONTHS.map((label, i) => ({
  title: label,
  key: `m${i}`,
  align: "center",
  render: (_, row) => row.monthly_status[i],
}));

const columns = [
  { title: "Periodical", dataIndex: "periodical_name", key: "periodical_name", fixed: "left" },
  { title: "Frequency", dataIndex: "frequency", key: "frequency" },
  ...monthColumns,
];

// Consolidated cross-facility status matrix — { state: { facility: [{periodical_name,
// frequency, monthly_status: 12 cells}] } } — a different (simpler, 2-level
// Group -> Facility, no Contract step) cascade than the rest of this module,
// matching the old ConsolidateReport.js container exactly.
const ConsolidateReport = () => {
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [facilityIds, setFacilityIds] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getGroups();
      setGroups(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const onGroupChange = useCallback(async (value) => {
    setGroupId(value);
    setFacilityIds([]);
    setFacilities([]);
    if (!value) return;
    const { error, result } = await getFacilitiesByGroup(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const search = useCallback(async () => {
    if (!groupId) {
      message.error("Please select a group first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    // "0" makes the backend report every facility in the group. Managers send
    // their own (token-scoped) facility list instead, so they only see those.
    const allFacilities = selectAll || facilityIds.length === 0;
    const manager = isManagerUser(parseInt(localStorage.getItem("is_super_admin") || "0") === 1, localStorage.getItem("user_type"));
    if (allFacilities && manager && facilities.length === 0) {
      message.info("No facilities assigned to you in this group.");
      return;
    }
    const facilityId = !allFacilities ? facilityIds.join(",") : manager ? facilities.map((f) => f.id).join(",") : "0";
    const { error, result } = await getPeriodicalStatusReport({ year: year.format("YYYY"), orgId: groupId, facilityId });
    setReport(!error && result?.result ? result.result : null);
    setLoading(false);
  }, [groupId, facilityIds, selectAll, year, facilities]);

  const states = report ? Object.keys(report) : [];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Group</div>
            <Select showSearch optionFilterProp="children" placeholder="Select a group" style={{ width: 200 }} value={groupId} onChange={onGroupChange}>
              {groups.map((g) => <Option key={g.id} value={g.id}>{g.name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facilities</div>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="children"
              placeholder="All facilities in group"
              style={{ width: 260 }}
              value={facilityIds}
              onChange={(v) => { setFacilityIds(v); setSelectAll(v.length === 0); }}
              disabled={!groupId}
              notFoundContent={groupId ? "No facilities for this group" : "Select a group first"}
            >
              {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={f.facility_id ?? f.id}>{f.facility_name ?? f.name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker picker="year" value={year} onChange={(d) => d && setYear(d)} allowClear={false} />
          </div>
          <Checkbox checked={selectAll} onChange={(e) => { setSelectAll(e.target.checked); if (e.target.checked) setFacilityIds([]); }}>
            All facilities
          </Checkbox>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
        </div>
      </div>

      {searched && (
        !report || states.length === 0 ? (
          <Empty description="No data for these filters." />
        ) : (
          states.map((state) => (
            <div key={state} style={{ marginBottom: 24 }}>
              <Title level={5}>{state}</Title>
              {Object.keys(report[state]).map((facility) => (
                <Card key={facility} title={facility} size="small" style={{ marginBottom: 12 }}>
                  <Table
                    rowKey={(_, i) => i}
                    columns={columns}
                    dataSource={report[state][facility]}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                  />
                </Card>
              ))}
            </div>
          ))
        )
      )}
    </div>
  );
};

export default ConsolidateReport;
